import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, Info } from "lucide-react";
import {
  STATIC_TYPE_OPTIONS,
  DATE_FORMAT_OPTIONS,
  StaticType,
  DateFormatType,
} from "@/types/variable-mappings";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface StaticFieldSelectorProps {
  selectedType?: StaticType;
  selectedFormat?: DateFormatType;
  onTypeChange: (type: StaticType) => void;
  onFormatChange: (format: DateFormatType) => void;
}

export function StaticFieldSelector({
  selectedType,
  selectedFormat,
  onTypeChange,
  onFormatChange,
}: StaticFieldSelectorProps) {
  // Obtener la opcion seleccionada para mostrar descripcion
  const selectedTypeOption = STATIC_TYPE_OPTIONS.find(opt => opt.value === selectedType);
  const selectedFormatOption = DATE_FORMAT_OPTIONS.find(opt => opt.value === selectedFormat);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Valor Automatico
        </CardTitle>
        <CardDescription>
          Configura un valor que se genera automaticamente al exportar el informe
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Informacion sobre valores automaticos */}
        <Alert className="border-green-200 bg-green-50/50">
          <Info className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-900 text-sm">
            <strong>Generacion Automatica:</strong> Este valor se calcula en el momento
            de generar el informe. Por ejemplo, la fecha actual mostrara la fecha
            en que se exporta el cuaderno.
          </AlertDescription>
        </Alert>

        {/* Selector de Tipo */}
        <div className="space-y-2">
          <Label>Tipo de Valor</Label>
          <Select value={selectedType} onValueChange={onTypeChange}>
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar tipo de valor" />
            </SelectTrigger>
            <SelectContent>
              {STATIC_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <div className="flex flex-col">
                    <span className="font-medium">{option.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {option.description}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Selector de Formato (solo para current_date) */}
        {selectedType === 'current_date' && (
          <div className="space-y-2">
            <Label>Formato de Fecha</Label>
            <Select value={selectedFormat || 'MMMM YYYY'} onValueChange={onFormatChange}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar formato" />
              </SelectTrigger>
              <SelectContent>
                {DATE_FORMAT_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{option.label}</span>
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                        {option.example}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Preview del valor */}
        {selectedType && (
          <div className="rounded-lg border bg-muted/50 p-4">
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="space-y-1 flex-1">
                <p className="font-medium text-sm">{selectedTypeOption?.label}</p>
                <p className="text-xs text-muted-foreground">{selectedTypeOption?.description}</p>
                {selectedType === 'current_date' && selectedFormatOption && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                      Formato: {selectedFormatOption.label}
                    </span>
                    <code className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded font-mono">
                      {selectedFormatOption.example}
                    </code>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Ejemplo de uso */}
        <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3 space-y-2">
          <p className="text-sm font-medium text-blue-900">Ejemplo de Uso:</p>
          <div className="text-xs text-blue-900 space-y-1">
            <p>1. Selecciona el tipo de valor (ej: <code className="bg-blue-100 px-1 rounded">Fecha Actual</code>)</p>
            <p>2. Elige el formato deseado (ej: <code className="bg-blue-100 px-1 rounded">Noviembre 2025</code>)</p>
            <p>3. En tu plantilla PPT, usa el placeholder de la variable (ej: <code className="bg-blue-100 px-1 rounded">{"{fechaExportacion}"}</code>)</p>
            <p>4. Al generar el informe, se mostrara la fecha actual con el formato elegido</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
